import React, { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import useReactApexChart from "../../hook/useReactApexChart";
import { api } from "../../services/api";

const UnitCountFive = () => {
  const [userData, setUserData] = useState({ count: 0, newThisMonth: 0 });
  const [examData, setExamData] = useState({ count: 0, newThisMonth: 0 });
  const [revenueData, setRevenueData] = useState({ total: 0, thisMonth: 0 });
  const [enrollmentData, setEnrollmentData] = useState({
    activeExams: 0,
    totalUsers: 0,
    monthlyEnrollments: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], // Jan to Dec
    monthlyUsers: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], // Jan to Dec
  });
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("Yearly");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all users
        const usersResponse = await api.users.getAll();
        if (usersResponse.success) {
          // Calculate new users this month
          const currentDate = new Date();
          const firstDayOfMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            1
          );

          const users = usersResponse.data || [];
          const newUsers = users.filter((user) => {
            const createdAt = new Date(user.createdAt);
            return createdAt >= firstDayOfMonth;
          });

          // Generate monthly enrollment data for chart
          const monthlyUserData = Array(12).fill(0);

          users.forEach((user) => {
            if (user.createdAt) {
              const createdAt = new Date(user.createdAt);
              const month = createdAt.getMonth();
              const year = createdAt.getFullYear();

              // Only count users created in the current year for the chart
              if (year === currentDate.getFullYear()) {
                monthlyUserData[month]++;
              }
            }
          });

          setUserData({
            count: usersResponse.count || users.length,
            newThisMonth: newUsers.length,
          });

          // Fetch all exams
          const examsResponse = await api.exams.getAll();
          if (examsResponse.success) {
            const exams = examsResponse.data || [];

            // Calculate new exams this month
            const newExams = exams.filter((exam) => {
              const createdAt = new Date(exam.createdAt);
              return createdAt >= firstDayOfMonth;
            });

            // Generate monthly exam data for chart
            const monthlyExamData = Array(12).fill(0);

            exams.forEach((exam) => {
              if (exam.createdAt) {
                const createdAt = new Date(exam.createdAt);
                const month = createdAt.getMonth();
                const year = createdAt.getFullYear();

                // Only count exams created in the current year for the chart
                if (year === currentDate.getFullYear()) {
                  monthlyExamData[month]++;
                }
              }
            });

            setExamData({
              count: examsResponse.count || exams.length,
              newThisMonth: newExams.length,
            });

            // Set combined enrollment data for the chart
            setEnrollmentData({
              activeExams: exams.length,
              totalUsers: users.length,
              monthlyEnrollments: monthlyExamData,
              monthlyUsers: monthlyUserData,
            });

            // Try to fetch revenue data from revenue-stats API
            try {
              const revenueResponse = await api.subscriptions.getRevenueStats();
              if (revenueResponse.success && revenueResponse.data) {
                const { totalRevenue, currentMonthRevenue } =
                  revenueResponse.data;

                // Use the correct structure based on API response
                const total =
                  totalRevenue && totalRevenue.totalRevenue
                    ? totalRevenue.totalRevenue
                    : 0;
                const thisMonth =
                  currentMonthRevenue && currentMonthRevenue.totalRevenue
                    ? currentMonthRevenue.totalRevenue
                    : 0;

                setRevenueData({
                  total,
                  thisMonth,
                });

                console.log("Revenue data loaded:", { total, thisMonth });
              } else {
                // If revenue data isn't available, fall back to estimated revenue
                const estimatedRevenuePerUser = 25; // Average $25 per user
                const totalRevenue = users.length * estimatedRevenuePerUser;
                const thisMonthRevenue =
                  newUsers.length * estimatedRevenuePerUser;

                setRevenueData({
                  total: totalRevenue,
                  thisMonth: thisMonthRevenue,
                });
                console.warn(
                  "Failed to fetch revenue data from API, using estimates"
                );
              }
            } catch (error) {
              console.error("Error fetching revenue data:", error);

              // Fallback to estimated revenue calculation
              const estimatedRevenuePerUser = 25; // Average $25 per user
              const totalRevenue = users.length * estimatedRevenuePerUser;
              const thisMonthRevenue =
                newUsers.length * estimatedRevenuePerUser;

              setRevenueData({
                total: totalRevenue,
                thisMonth: thisMonthRevenue,
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get the chart component with updated series data
  const getChart = () => {
    // Filter data based on timeframe
    let filteredExamData = [...enrollmentData.monthlyEnrollments];
    let filteredUserData = [...enrollmentData.monthlyUsers];
    let categories = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    if (timeframe === "Monthly") {
      // Show only data for the current month, broken down by weeks
      categories = ["Week 1", "Week 2", "Week 3", "Week 4"];

      // Simulate weekly data for current month (since we only have monthly data)
      const examMonthData = enrollmentData.monthlyEnrollments[currentMonth];
      const userMonthData = enrollmentData.monthlyUsers[currentMonth];

      // Distribute monthly data across weeks (simplified approach)
      filteredExamData = [
        Math.round(examMonthData * 0.2),
        Math.round(examMonthData * 0.3),
        Math.round(examMonthData * 0.3),
        Math.round(examMonthData * 0.2),
      ];

      filteredUserData = [
        Math.round(userMonthData * 0.2),
        Math.round(userMonthData * 0.3),
        Math.round(userMonthData * 0.3),
        Math.round(userMonthData * 0.2),
      ];
    } else if (timeframe === "Weekly") {
      // Show only data for the current week, broken down by days
      categories = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

      // Simulate daily data for current week (since we only have monthly data)
      const examMonthData = enrollmentData.monthlyEnrollments[currentMonth];
      const userMonthData = enrollmentData.monthlyUsers[currentMonth];

      // Distribute monthly data across days (simplified approach)
      filteredExamData = [
        Math.round(examMonthData * 0.15),
        Math.round(examMonthData * 0.2),
        Math.round(examMonthData * 0.18),
        Math.round(examMonthData * 0.15),
        Math.round(examMonthData * 0.12),
        Math.round(examMonthData * 0.1),
        Math.round(examMonthData * 0.1),
      ];

      filteredUserData = [
        Math.round(userMonthData * 0.15),
        Math.round(userMonthData * 0.2),
        Math.round(userMonthData * 0.18),
        Math.round(userMonthData * 0.15),
        Math.round(userMonthData * 0.12),
        Math.round(userMonthData * 0.1),
        Math.round(userMonthData * 0.1),
      ];
    } else if (timeframe === "Today") {
      // Show only data for today, broken down by hours
      categories = ["8AM", "10AM", "12PM", "2PM", "4PM", "6PM", "8PM"];

      // Simulate hourly data for today (simplified approach)
      const examMonthData = enrollmentData.monthlyEnrollments[currentMonth];
      const userMonthData = enrollmentData.monthlyUsers[currentMonth];

      // Create a daily pattern (more activity during business hours)
      filteredExamData = [
        Math.round((examMonthData * 0.05) / 30),
        Math.round((examMonthData * 0.15) / 30),
        Math.round((examMonthData * 0.2) / 30),
        Math.round((examMonthData * 0.25) / 30),
        Math.round((examMonthData * 0.2) / 30),
        Math.round((examMonthData * 0.1) / 30),
        Math.round((examMonthData * 0.05) / 30),
      ];

      filteredUserData = [
        Math.round((userMonthData * 0.05) / 30),
        Math.round((userMonthData * 0.15) / 30),
        Math.round((userMonthData * 0.2) / 30),
        Math.round((userMonthData * 0.25) / 30),
        Math.round((userMonthData * 0.2) / 30),
        Math.round((userMonthData * 0.1) / 30),
        Math.round((userMonthData * 0.05) / 30),
      ];
    }

    // Create a modified version of the chart that uses our real data
    let series = [
      {
        name: "Exams",
        data: filteredExamData,
      },
      {
        name: "Users",
        data: filteredUserData,
      },
    ];

    let options = {
      legend: {
        show: false,
      },
      chart: {
        type: "area",
        width: "100%",
        height: 270,
        toolbar: {
          show: false,
        },
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 3,
        colors: ["#45B369", "#487fff"], // Use two colors for the lines
        lineCap: "round",
      },
      grid: {
        show: true,
        borderColor: "#D1D5DB",
        strokeDashArray: 1,
        position: "back",
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        row: {
          colors: undefined,
          opacity: 0.5,
        },
        column: {
          colors: undefined,
          opacity: 0.5,
        },
        padding: {
          top: -20,
          right: 0,
          bottom: -10,
          left: 0,
        },
      },
      fill: {
        type: "gradient",
        colors: ["#45B369", "#487fff"],
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.5,
          gradientToColors: [undefined, `#487fff00`], // Apply transparency to both colors
          inverseColors: false,
          opacityFrom: [0.4, 0.4], // Starting opacity for both colors
          opacityTo: [0.3, 0.3], // Ending opacity for both colors
          stops: [0, 100],
        },
      },
      markers: {
        colors: ["#45B369", "#487fff"], // Use two colors for the markers
        strokeWidth: 3,
        size: 0,
        hover: {
          size: 10,
        },
      },
      xaxis: {
        categories: categories,
        tooltip: {
          enabled: false,
        },
        labels: {
          formatter: function (value) {
            return value;
          },
          style: {
            fontSize: "14px",
          },
        },
      },
      yaxis: {
        labels: {
          formatter: function (value) {
            return value;
          },
          style: {
            fontSize: "14px",
          },
        },
      },
      tooltip: {
        x: {
          format: "dd/MM/yy HH:mm",
        },
      },
    };

    return { options, series };
  };

  // Original hook for the chart (will be replaced with our custom chart)
  let { createChartSix } = useReactApexChart();

  // Get our custom chart data
  const customChart = useMemo(() => getChart(), [enrollmentData, timeframe]);

  return (
    <div className="col-xxl-8">
      <div className="card radius-8 border-0 p-20">
        <div className="row gy-4">
          <div className="col-xxl-4">
            <div className="card p-3 radius-8 shadow-none bg-gradient-dark-start-1 mb-12">
              <div className="card-body p-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-0">
                  <div className="d-flex align-items-center gap-2 mb-12">
                    <span className="mb-0 w-48-px h-48-px bg-base text-pink text-2xl flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle h6">
                      <i className="ri-group-fill" />
                    </span>
                    <div>
                      <span className="mb-0 fw-medium text-secondary-light text-lg">
                        Total Students
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-8">
                  <h5 className="fw-semibold mb-0">
                    {loading ? "Loading..." : userData.count}
                  </h5>
                  <p className="text-sm mb-0 d-flex align-items-center gap-8">
                    <span className="text-white px-1 rounded-2 fw-medium bg-success-main text-sm">
                      +{loading ? "..." : userData.newThisMonth}
                    </span>
                    This Month
                  </p>
                </div>
              </div>
            </div>
            <div className="card p-3 radius-8 shadow-none bg-gradient-dark-start-2 mb-12">
              <div className="card-body p-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-0">
                  <div className="d-flex align-items-center gap-2 mb-12">
                    <span className="mb-0 w-48-px h-48-px bg-base text-purple text-2xl flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle h6">
                      <i className="ri-file-list-3-fill" />
                    </span>
                    <div>
                      <span className="mb-0 fw-medium text-secondary-light text-lg">
                        Total Exams
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-8">
                  <h5 className="fw-semibold mb-0">
                    {loading ? "Loading..." : examData.count}
                  </h5>
                  <p className="text-sm mb-0 d-flex align-items-center gap-8">
                    <span className="text-white px-1 rounded-2 fw-medium bg-success-main text-sm">
                      +{loading ? "..." : examData.newThisMonth}
                    </span>
                    This Month
                  </p>
                </div>
              </div>
            </div>
            <div className="card p-3 radius-8 shadow-none bg-gradient-dark-start-3 mb-0">
              <div className="card-body p-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-0">
                  <div className="d-flex align-items-center gap-2 mb-12">
                    <span className="mb-0 w-48-px h-48-px bg-base text-info text-2xl flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle h6">
                      <i className="ri-money-dollar-circle-fill" />
                    </span>
                    <div>
                      <span className="mb-0 fw-medium text-secondary-light text-lg">
                        Overall Revenue
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-8">
                  <h5 className="fw-semibold mb-0">
                    {loading ? "Loading..." : formatCurrency(revenueData.total)}
                  </h5>
                  <p className="text-sm mb-0 d-flex align-items-center gap-8">
                    <span className="text-white px-1 rounded-2 fw-medium bg-success-main text-sm">
                      +{loading ? "..." : formatCurrency(revenueData.thisMonth)}
                    </span>
                    This Month
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xxl-8">
            <div className="card-body p-0">
              <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
                <h6 className="mb-2 fw-bold text-lg">
                  Average Enrollment Rate
                </h6>
                <div className="">
                  <select
                    className="form-select form-select-sm w-auto bg-base border text-secondary-light"
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                  >
                    <option value="Yearly">Yearly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Today">Today</option>
                  </select>
                </div>
              </div>
              <ul className="d-flex flex-wrap align-items-center justify-content-center mt-3 gap-3">
                <li className="d-flex align-items-center gap-2">
                  <span className="w-12-px h-12-px rounded-circle bg-success-main" />
                  <span className="text-secondary-light text-sm fw-semibold">
                    Active Exams:
                    <span className="text-primary-light fw-bold">
                      {loading ? "..." : enrollmentData.activeExams}
                    </span>
                  </span>
                </li>
                <li className="d-flex align-items-center gap-2">
                  <span className="w-12-px h-12-px rounded-circle bg-primary-600 " />
                  <span className="text-secondary-light text-sm fw-semibold ">
                    Registered Users:
                    <span className="text-primary-light fw-bold">
                      {loading ? "..." : enrollmentData.totalUsers}
                    </span>
                  </span>
                </li>
              </ul>
              <div className="mt-40">
                <div
                  id="enrollmentChart"
                  className="apexcharts-tooltip-style-1"
                >
                  {loading ? (
                    <div className="text-center py-4">
                      Loading chart data...
                    </div>
                  ) : (
                    <ReactApexChart
                      options={customChart.options}
                      series={customChart.series}
                      type="area"
                      height={270}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitCountFive;
